import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  CheckIcon,
  CloseIcon,
  Input,
  Spinner,
} from "apps/portal/src/components/atoms";
import { ModalWrapper } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { XIcon } from "../../atoms/icons/XIcon";
import { ManageItem } from "./components/ManageItem";

type ManageModalProps<TItem extends { id: number; name: string }> = {
  onHide: () => void;
  toastMessage: string;
  title: string;
  description: string;
  items: TItem[];
  isLoadingList: boolean;
  isPendingAdd: boolean;
  isPendingEdit: boolean;
  onAdd: (name: string) => Promise<void>;
  onEdit: (id: number, name: string) => Promise<void>;
  onDeleteItem: (item: TItem) => Promise<void> | void;
};

export const ManageModal = <TItem extends { id: number; name: string }>({
  onHide,
  toastMessage,
  title,
  description,
  items,
  isLoadingList,
  isPendingAdd,
  isPendingEdit,
  onAdd,
  onEdit,
  onDeleteItem,
}: ManageModalProps<TItem>) => {
  const [input, setInput] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();

  const { t } = useTranslation();
  const { toast } = useContext(ToastContext);

  const onClickAdd = useCallback(async () => {
    await onAdd(input);
    setInput("");
    toast({
      message: toastMessage,
      type: "small",
      intent: "success",
    });
  }, [input, onAdd, toast, toastMessage]);

  const onStartEdit = useCallback(async ({ name, id }: TItem) => {
    setInput(name);
    setEditingId(id);
    setIsEditing(true);
  }, []);

  const onFinishEdit = useCallback(async () => {
    setIsEditing(false);
    setInput("");
    setEditingId(undefined);
  }, []);

  const onEditItem = useCallback(async () => {
    if (!editingId) {
      return;
    }
    await onEdit(editingId, input);
    toast({
      message: t("components.manageModal.toast.changesSaved"),
      type: "small",
      intent: "success",
    });
    onFinishEdit();
  }, [editingId, onEdit, input, toast, t, onFinishEdit]);

  return (
    <ModalWrapper
      className="flex flex-col gap-4 sm:!max-w-xl"
      onCancel={() => onHide()}
    >
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col">
          <div className="text-lg font-semibold text-gray-950">{title}</div>
          <div className="text-sm font-medium text-gray-500">{description}</div>
        </div>
        <div className="cursor-pointer" onClick={() => onHide()}>
          <CloseIcon className="size-6" />
        </div>
      </div>
      <div className="flex flex-row items-center gap-4">
        <Input
          value={input}
          disabled={isPendingAdd}
          onChange={(e) => setInput(e.currentTarget.value)}
          placeholder={t("components.manageModal.input.placeholder")}
        />
        <div className="flew-row flex w-[5.625rem] gap-1.5">
          {isEditing ? (
            <>
              <Button
                intent="secondary-outline"
                className="h-10 w-full"
                onClick={async () => await onEditItem()}
              >
                <div>
                  <CheckIcon className="size-4" />
                </div>
              </Button>
              <Button
                intent="default"
                className="h-10 w-full"
                disabled={isPendingEdit}
                onClick={() => onFinishEdit()}
              >
                <div>
                  <XIcon className="size-5" pathClassName="stroke-gray-950" />
                </div>
              </Button>
            </>
          ) : (
            <Button
              intent="secondary"
              className="text-gray-0 h-10 w-[5.625rem] gap-x-1.5"
              disabled={!input || isPendingAdd}
              onClick={async () => await onClickAdd()}
            >
              <FontAwesomeIcon icon={faPlus} />
              {t("portal.common.add")}
            </Button>
          )}
        </div>
      </div>
      <div className="h-72 overflow-y-auto">
        {isLoadingList ? (
          <Spinner />
        ) : (
          <div className="flex flex-col">
            {items?.map((item) => (
              <ManageItem
                key={item.id}
                item={item}
                isEditing={editingId === item.id}
                onStartEdit={async (item) => await onStartEdit(item)}
                onDeleteItem={onDeleteItem}
              />
            ))}
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};
